import React, { useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ArrowRight } from 'lucide-react';
interface RewriteCompleteModalProps {
  isOpen: boolean;
  score?: number;
  onViewResults: () => void;
  onClose: () => void;
}
export function RewriteCompleteModal({
  isOpen,
  score = 0,
  onViewResults,
  onClose
}: RewriteCompleteModalProps) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const themeColor = '#00C18E';
  const assessmentLabel = 'Message\nOptimized';
  return <AnimatePresence data-id="element-2128">
      {isOpen && <motion.div key="rewrite-complete-modal-backdrop" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(8px)'
    }} onClick={e => {
      if (e.target === e.currentTarget) onClose();
    }} data-id="element-2129">
          <motion.div key="rewrite-complete-modal-card" initial={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 30
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-md bg-[#050505] overflow-hidden p-8 sm:p-10" style={{
        '--theme-color': themeColor,
        '--theme-color-10': `${themeColor}10`,
        '--theme-color-15': `${themeColor}15`,
        '--theme-color-20': `${themeColor}20`,
        border: '1px solid var(--theme-color)',
        boxShadow: '0 0 40px var(--theme-color-20), 0 0 80px var(--theme-color-10)'
      } as React.CSSProperties} onClick={e => e.stopPropagation()} data-id="element-2130">
            {/* Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-0" data-id="element-2131" />

            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 z-10" style={{
          borderColor: themeColor
        }} data-id="element-2132" />
            <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 z-10" style={{
          borderColor: themeColor
        }} data-id="element-2133" />
            <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 z-10" style={{
          borderColor: themeColor
        }} data-id="element-2134" />
            <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 z-10" style={{
          borderColor: themeColor
        }} data-id="element-2135" />

            <div className="relative z-10 flex flex-col items-center text-center" data-id="element-2136">
              {/* Animated Icon */}
              <motion.div className="mb-8 flex justify-center" initial={{
            scale: 0,
            rotate: -20
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.15
          }} data-id="element-2137">
                <div className="p-5 rounded-full border-2 bg-[#050505]" style={{
              borderColor: 'var(--theme-color)',
              boxShadow: 'inset 0 0 20px var(--theme-color-20), 0 0 20px var(--theme-color-15)'
            }} data-id="element-2138">
                  <ShieldCheck size={48} color={themeColor} strokeWidth={1.5} data-id="element-2139" />
                </div>
              </motion.div>

              <motion.h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-8" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} data-id="element-2140">
                Rewrite Complete
              </motion.h2>

              <motion.div className="w-full py-6 border-y border-gray-800/60 mb-8 flex items-center justify-center gap-6" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.35
          }} data-id="element-2141">
                <div className="text-right flex-1" data-id="element-2142">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-2143">
                    Health Score
                  </p>
                  <p className="text-4xl sm:text-5xl font-black tracking-tighter" style={{
                color: themeColor
              }} data-id="element-2144">
                    +{score}%
                  </p>
                </div>
                <div className="h-16 w-px bg-gray-800/60" data-id="element-2145" />
                <div className="text-left flex-1" data-id="element-2146">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-2147">
                    Assessment
                  </p>
                  <p className="text-lg sm:text-xl font-bold leading-snug" style={{
                color: themeColor
              }} data-id="element-2148">
                    {assessmentLabel.split('\n').map((line, i) => <Fragment key={i} data-id="element-2149">
                        {line}
                        {i === 0 && <br data-id="element-2150" />}
                      </Fragment>)}
                  </p>
                </div>
              </motion.div>

              <motion.div className="w-full space-y-5" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} data-id="element-2151">
                <button onClick={onViewResults} className="w-full py-4 font-bold text-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{
              backgroundColor: themeColor
            }} data-id="element-2152">
                  View Results
                  <ArrowRight size={20} data-id="element-2153" />
                </button>

                <button onClick={onClose} className="w-full py-2 text-gray-500 hover:text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors" data-id="element-2154">
                  Dismiss
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}